import {
  Card,
  CardContent,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  CardHeader,
  CardActions,
  Button,
  ThemeProvider,
  createTheme,
  Box,
  useMediaQuery,
  type SxProps,
  type Theme,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useTheme } from '@mui/material/styles';
import { narrowHeaderIconButtonSx, widgetHeaderActionSpacing } from '@/components/common/widget-header-action-styles';

export interface IconButtonConfig {
  icon: ReactNode;
  /** When set, xs shows icon-only; sm+ shows outlined button with label. */
  label?: string;
  onClick?: () => void;
  disabled?: boolean;
  testId?: string;
  tooltip?: string;
  color?: 'info' | 'primary' | 'secondary' | 'default';
  sx?: SxProps<Theme>;
}

export interface PrimaryButtonConfig {
  label: string;
  onClick?: () => void;
  testId?: string;
  id?: string;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  disabled?: boolean;
  variant?: 'text' | 'outlined' | 'contained';
}

interface WidgetProps {
  'title'?: string;
  'titleVariant'?: 'subtitle1' | 'h6';
  'subtitle'?: string;
  'showTitle'?: boolean;
  'primaryButton'?: PrimaryButtonConfig | ReactNode;
  'iconButtons'?: IconButtonConfig[] | ReactNode;
  'leftControls'?: ReactNode;
  'children'?: ReactNode;
  'actions'?: ReactNode;
  'data-testid'?: string;
  'sx'?: SxProps<Theme>;
  /** Merged into `CardContent` after base padding styles. */
  'cardContentSx'?: SxProps<Theme>;
}

export default function Widget({
  title,
  titleVariant = 'h6',
  subtitle,
  showTitle = true,
  primaryButton,
  iconButtons,
  leftControls,
  children,
  actions,
  'data-testid': testId,
  sx,
  cardContentSx,
}: WidgetProps) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const widgetThemeOverrides = createTheme(theme, {
    components: {
      MuiTextField: {
        defaultProps: {
          size: 'small',
          color: 'secondary',
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'secondary.main',
            },
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          size: 'small',
          color: 'secondary',
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'secondary.main',
            },
          },
        },
      },
      MuiInputLabel: {
        defaultProps: {
          color: 'secondary',
        },
      },
    },
  });

  return (
    <Card
      data-testid={testId}
      variant="outlined"
      sx={{
        'border': 1,
        'borderColor': 'divider',
        'borderRadius': 2,
        'boxShadow': 1,
        'backgroundColor': (theme) => theme.palette.background.elevation1,
        'transition': 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          boxShadow: 2,
          borderColor: 'divider',
        },
        ...sx,
      }}
    >
      {(() => {
        const hasTitle = showTitle && title;
        const hasActions = !!(primaryButton || iconButtons);
        const hasLeftControls = !!leftControls;
        const shouldShowHeader = hasTitle || hasActions || hasLeftControls;

        if (!shouldShowHeader) return null;

        return (
          <CardHeader
            sx={{
              'paddingBottom': 0,
              'paddingTop': (hasTitle || hasLeftControls) && (hasTitle || hasActions || hasLeftControls) ? 1 : 0,
              'paddingX': hasTitle || hasActions || hasLeftControls ? 1 : 0,
              'paddingRight': hasTitle || hasActions || hasLeftControls ? 2 : 0,
              'minHeight': (hasTitle || hasLeftControls) && (hasTitle || hasActions || hasLeftControls) ? '32px' : 0,
              'display': hasTitle || hasActions || hasLeftControls ? 'flex' : 'none',
              '& .MuiCardHeader-content': {
                paddingTop: 0,
                display: hasTitle || hasLeftControls ? 'flex' : 'none',
                alignItems: 'center',
              },
              '& .MuiCardHeader-action': {
                marginTop: 0,
                marginLeft: 'auto',
                alignSelf: 'center',
                display: hasActions ? 'flex' : 'none',
              },
            }}
            title={
              hasTitle ? (
                <Stack direction="row" spacing={3} alignItems="center">
                  <Typography variant={titleVariant} color="info" component="h2" sx={{ fontWeight: 700 }}>
                    {title}
                  </Typography>
                  {hasLeftControls && <Box sx={{ minWidth: 'auto' }}>{leftControls}</Box>}
                </Stack>
              ) : hasLeftControls ? (
                <Box sx={{ minWidth: 'auto' }}>{leftControls}</Box>
              ) : null
            }
            subheader={hasTitle ? subtitle : undefined}
            action={
              hasActions ? (
                <Stack direction="row" spacing={widgetHeaderActionSpacing(isXs)} alignItems="center">
                  {Array.isArray(iconButtons)
                    ? iconButtons.map((button, index) => {
                        const color = button.color === 'default' || button.color == null ? 'secondary' : button.color;

                        if (button.label) {
                          const labeledAction = isXs ? (
                            <Tooltip title={button.tooltip ?? button.label} disableTouchListener>
                              <span>
                                <IconButton
                                  data-testid={button.testId}
                                  disabled={button.disabled}
                                  size="small"
                                  color={color}
                                  onClick={button.onClick}
                                  sx={
                                    button.sx
                                      ? ([narrowHeaderIconButtonSx, button.sx] as SxProps<Theme>)
                                      : narrowHeaderIconButtonSx
                                  }
                                >
                                  {button.icon}
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <Button
                              data-testid={button.testId}
                              variant="outlined"
                              size="small"
                              color={color}
                              onClick={button.onClick}
                              disabled={button.disabled}
                              startIcon={button.icon}
                            >
                              {button.label}
                            </Button>
                          );

                          return <span key={index}>{labeledAction}</span>;
                        }

                        const iconButton = (
                          <span style={{ display: 'inline-block' }}>
                            <IconButton
                              data-testid={button.testId}
                              disabled={button.disabled}
                              size="small"
                              color={button.color || 'default'}
                              onClick={button.onClick}
                              sx={
                                button.sx
                                  ? ([narrowHeaderIconButtonSx, button.sx] as SxProps<Theme>)
                                  : narrowHeaderIconButtonSx
                              }
                            >
                              {button.icon}
                            </IconButton>
                          </span>
                        );

                        return button.tooltip ? (
                          <Tooltip key={index} title={button.tooltip} disableTouchListener>
                            {iconButton}
                          </Tooltip>
                        ) : (
                          <span key={index}>{iconButton}</span>
                        );
                      })
                    : iconButtons}
                  {primaryButton &&
                    (typeof primaryButton === 'object' && 'label' in primaryButton ? (
                      isXs && primaryButton.startIcon ? (
                        <Tooltip title={primaryButton.label} disableTouchListener>
                          <span>
                            <IconButton
                              data-testid={primaryButton.testId}
                              id={primaryButton.id}
                              size="small"
                              color="secondary"
                              onClick={primaryButton.onClick}
                              disabled={primaryButton.disabled}
                              sx={narrowHeaderIconButtonSx}
                            >
                              {primaryButton.startIcon}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <Button
                          data-testid={primaryButton.testId}
                          id={primaryButton.id}
                          variant={primaryButton.variant || 'outlined'}
                          onClick={primaryButton.onClick}
                          startIcon={primaryButton.startIcon}
                          endIcon={primaryButton.endIcon}
                          disabled={primaryButton.disabled}
                          size="small"
                          color="secondary"
                        >
                          {primaryButton.label}
                        </Button>
                      )
                    ) : (
                      primaryButton
                    ))}
                </Stack>
              ) : null
            }
          />
        );
      })()}
      <CardContent
        sx={
          [
            {
              'paddingTop': 1,
              'paddingBottom': 0,
              'paddingX': 1,
              '&:last-child': {
                paddingBottom: 1.5,
              },
              '& .MuiCard-root': {
                // Reset tile style for Cards inside Widget
                'border': 'none',
                'boxShadow': 'none',
                'borderRadius': 0,
                'backgroundColor': 'transparent',
                '&:hover': {
                  boxShadow: 'none',
                },
              },
            },
            ...(cardContentSx ? (Array.isArray(cardContentSx) ? cardContentSx : [cardContentSx]) : []),
          ] as SxProps<Theme>
        }
      >
        <ThemeProvider theme={widgetThemeOverrides}>{children}</ThemeProvider>
      </CardContent>
      {actions && <CardActions sx={{ padding: 1 }}>{actions}</CardActions>}
    </Card>
  );
}
